import { useState, useEffect, useMemo } from 'react';
import { unlinkedApi, schoolsApi, roomsApi } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Cpu, Link2, CheckCircle2, Loader2, Wifi, Radio, AlertCircle, Trash2, Clock, Activity,
} from 'lucide-react';

const relTime = (iso) => {
  if (!iso) return 'aldrig';
  const d = new Date(iso);
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'nyss';
  if (mins < 60) return `för ${mins} min sedan`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `för ${h} tim sedan`;
  return `för ${Math.floor(h / 24)} dygn sedan`;
};

const connBadge = (status) => {
  if (status === 'online') return { label: 'Online', cls: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' };
  if (status === 'warning') return { label: 'Tyst', cls: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' };
  return { label: 'Offline', cls: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300' };
};

export default function UnlinkedSensorsPage() {
  const [sensors, setSensors] = useState([]);
  const [loading, setLoading] = useState(true);

  // Link dialog state
  const [linkOpen, setLinkOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [schools, setSchools] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [schoolId, setSchoolId] = useState('');
  const [roomId, setRoomId] = useState('');
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [targetSlot, setTargetSlot] = useState('');
  const [newName, setNewName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await unlinkedApi.list();
      setSensors(res.data.sensors || []);
    } catch {
      toast.error('Kunde inte ladda okopplade sensorer');
    } finally {
      setLoading(false);
    }
  };

  const openLink = async (sensor) => {
    setActive(sensor);
    setSchoolId(''); setRoomId(''); setRooms([]); setSlots([]); setTargetSlot(''); setNewName('');
    setLinkOpen(true);
    if (schools.length === 0) {
      try {
        const res = await schoolsApi.list();
        setSchools(res.data || []);
      } catch { toast.error('Kunde inte ladda skolor'); }
    }
  };

  const onSchoolChange = async (id) => {
    setSchoolId(id); setRoomId(''); setRooms([]); setSlots([]); setTargetSlot('');
    if (!id) return;
    try {
      const res = await roomsApi.list(id);
      setRooms(res.data || []);
    } catch { toast.error('Kunde inte ladda rum'); }
  };

  const onRoomChange = async (id) => {
    setRoomId(id); setSlots([]); setTargetSlot(''); setNewName('');
    if (!id) return;
    setSlotsLoading(true);
    try {
      const res = await unlinkedApi.roomSlots(id);
      const s = res.data.slots || [];
      setSlots(s);
      if (s.length === 1) setTargetSlot(s[0].id);
      else if (s.length === 0) setTargetSlot('__new__');
    } catch {
      toast.error('Kunde inte ladda sensorplatser');
    } finally {
      setSlotsLoading(false);
    }
  };

  const submitLink = async () => {
    if (!roomId) { toast.error('Välj ett rum'); return; }
    const body = { room_id: roomId };
    if (targetSlot === '__new__') {
      body.create_new = true;
      if (newName.trim()) body.name = newName.trim();
    } else if (targetSlot) {
      body.target_device_id = targetSlot;
    } else {
      toast.error('Välj en sensorplats eller skapa en ny');
      return;
    }
    setSubmitting(true);
    try {
      const res = await unlinkedApi.link(active.id, body);
      toast.success(res.data.message || 'Sensor kopplad');
      setLinkOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte koppla sensor');
    } finally {
      setSubmitting(false);
    }
  };

  const discard = async (sensor) => {
    if (!window.confirm(`Ta bort okopplad sensor ${sensor.device_eui}? Lagrade avläsningar raderas.`)) return;
    try {
      await unlinkedApi.delete(sensor.id);
      toast.success('Okopplad sensor borttagen');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte ta bort');
    }
  };

  const total = sensors.length;
  const canCreateNew = useMemo(() => roomId && !slotsLoading, [roomId, slotsLoading]);

  if (loading) {
    return <div className="p-6 md:p-10 flex items-center justify-center min-h-[60vh]"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>;
  }

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="unlinked-sensors-page">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Cpu className="h-5 w-5 text-slate-400" />
          <span className="text-xs font-ibm uppercase tracking-widest text-slate-400">Onboarding</span>
        </div>
        <h1 className="font-chivo font-black text-2xl md:text-3xl text-navy dark:text-white tracking-tight">
          Okopplade sensorer
        </h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1 font-ibm text-sm max-w-2xl">
          Sensorer som börjat skicka data men ännu inte placerats i ett rum. De genererar inga larm
          och räknas inte som aktiva enheter förrän de kopplas till en plats.
        </p>
      </div>

      {total === 0 ? (
        <Card className="border-slate-200 dark:border-slate-700">
          <CardContent className="py-16 text-center">
            <CheckCircle2 className="h-14 w-14 text-emerald-400 mx-auto mb-4" />
            <h2 className="font-chivo font-bold text-lg text-navy dark:text-white">Inga okopplade sensorer</h2>
            <p className="text-slate-500 dark:text-slate-400 font-ibm text-sm mt-1">
              Alla sensorer som rapporterat är kopplade till ett rum.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <Badge variant="secondary" className="bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 font-ibm">
            <AlertCircle className="h-3 w-3 mr-1" />
            {total} {total === 1 ? 'sensor väntar' : 'sensorer väntar'} på koppling
          </Badge>

          <div className="space-y-3">
            {sensors.map((s) => {
              const cb = connBadge(s.connectivity_status);
              const isWifi = (s.connection_type || 'wifi') === 'wifi';
              const lr = s.last_reading || {};
              return (
                <Card key={s.id} className="border-slate-200 dark:border-slate-700" data-testid={`unlinked-sensor-${s.id}`}>
                  <CardContent className="p-4">
                    <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-10 h-10 rounded-lg bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center shrink-0">
                          {isWifi ? <Wifi className="h-5 w-5 text-amber-500" /> : <Radio className="h-5 w-5 text-amber-500" />}
                        </div>
                        <div className="min-w-0">
                          <p className="font-mono font-semibold text-sm text-navy dark:text-white truncate" data-testid={`unlinked-eui-${s.id}`}>
                            {s.device_eui}
                          </p>
                          <div className="flex flex-wrap items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-[10px] font-ibm">{isWifi ? 'WiFi' : 'LoRaWAN'}</Badge>
                            <Badge className={`text-[10px] font-ibm border-0 ${cb.cls}`}>{cb.label}</Badge>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-5 gap-y-1 text-xs font-ibm flex-1">
                        <div>
                          <span className="text-slate-400 block">Först sedd</span>
                          <span className="text-slate-600 dark:text-slate-300 flex items-center gap-1"><Clock className="h-3 w-3" />{relTime(s.first_seen)}</span>
                        </div>
                        <div>
                          <span className="text-slate-400 block">Senast sedd</span>
                          <span className="text-slate-600 dark:text-slate-300 flex items-center gap-1"><Clock className="h-3 w-3" />{relTime(s.last_seen)}</span>
                        </div>
                        <div>
                          <span className="text-slate-400 block">Avläsningar</span>
                          <span className="text-slate-600 dark:text-slate-300 flex items-center gap-1"><Activity className="h-3 w-3" />{s.readings_count ?? 0}</span>
                        </div>
                        <div>
                          <span className="text-slate-400 block">Senaste värden</span>
                          <span className="text-slate-600 dark:text-slate-300">
                            {lr.pm2_5 != null ? `PM2.5 ${lr.pm2_5}` : '–'}{lr.voc != null ? ` · VOC ${lr.voc}` : ''}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        <Button onClick={() => openLink(s)} className="gap-1.5 h-9" data-testid={`link-unlinked-btn-${s.id}`}>
                          <Link2 className="h-4 w-4" /> Koppla
                        </Button>
                        <Button variant="ghost" size="icon" className="h-9 w-9 text-slate-400 hover:text-red-500" onClick={() => discard(s)} data-testid={`discard-unlinked-btn-${s.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </>
      )}

      {/* Link dialog */}
      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent className="sm:max-w-md" data-testid="link-dialog">
          <DialogHeader>
            <DialogTitle className="font-chivo">Koppla sensor</DialogTitle>
            <DialogDescription className="font-ibm font-mono text-xs">{active?.device_eui}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <label className="text-xs font-ibm font-semibold text-slate-500 block mb-1">Kund / Skola</label>
              <select
                value={schoolId}
                onChange={(e) => onSchoolChange(e.target.value)}
                className="w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm font-ibm"
                data-testid="link-school-select"
              >
                <option value="">Välj skola…</option>
                {schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>

            <div>
              <label className="text-xs font-ibm font-semibold text-slate-500 block mb-1">Rum / Plats</label>
              <select
                value={roomId}
                onChange={(e) => onRoomChange(e.target.value)}
                disabled={!schoolId}
                className="w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm font-ibm disabled:opacity-50"
                data-testid="link-room-select"
              >
                <option value="">Välj rum…</option>
                {rooms.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}{r.building ? ` · ${r.building}` : ''}{r.floor ? ` · plan ${r.floor}` : ''}
                  </option>
                ))}
              </select>
            </div>

            {slotsLoading && (
              <div className="flex items-center gap-2 text-sm text-slate-400 font-ibm"><Loader2 className="h-4 w-4 animate-spin" /> Hämtar sensorplatser…</div>
            )}

            {canCreateNew && (
              <div>
                <label className="text-xs font-ibm font-semibold text-slate-500 block mb-1">Sensorplats</label>
                {slots.length > 0 && (
                  <p className="text-[11px] font-ibm text-slate-400 mb-1">
                    {slots.length === 1 ? 'En ledig förberedd plats hittades.' : `${slots.length} lediga platser — välj en.`}
                  </p>
                )}
                <select
                  value={targetSlot}
                  onChange={(e) => setTargetSlot(e.target.value)}
                  className="w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm font-ibm"
                  data-testid="link-slot-select"
                >
                  {slots.length !== 1 && <option value="">Välj…</option>}
                  {slots.map((sl) => (
                    <option key={sl.id} value={sl.id}>{sl.name || 'Förberedd plats'}{sl.department ? ` (${sl.department})` : ''}</option>
                  ))}
                  <option value="__new__">+ Skapa ny sensorplats</option>
                </select>

                {targetSlot === '__new__' && (
                  <Input
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="Namn på ny plats (valfritt)"
                    className="mt-2 h-9 text-sm font-ibm"
                    data-testid="link-new-name"
                  />
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkOpen(false)} disabled={submitting}>Avbryt</Button>
            <Button onClick={submitLink} disabled={submitting || !roomId} className="gap-1.5" data-testid="link-confirm-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
              Koppla sensor
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
